import MerakliMobileControl from "../components/MerakliMobileControl";
import MerakliLiveStatus from "../components/MerakliLiveStatus";
import { useEffect, useState } from "react";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import { apiRequest } from "@/lib/api";

type MerakliPolicy = {
  study_minutes_required: number;
  reward_minutes: number;
  enforced_study_mode: number | boolean;
  shutdown_on_exit: number | boolean;
  warning_minutes: number;
  policy_version: number;
  updated_at?: string | null;
};

type MerakliReport = {
  summaries: Array<{ summary_date: string; focus_minutes: number; break_minutes: number; lesson_minutes: number; completed_task_count: number }>;
  tasks: Array<{ title: string; subject: string | null; status: string; estimated_minutes: number | null }>;
  review_topics: Array<{ subject: string; topic: string; question_count: number }>;
};

const defaults: MerakliPolicy = {
  study_minutes_required: 60,
  reward_minutes: 15,
  enforced_study_mode: false,
  shutdown_on_exit: false,
  warning_minutes: 5,
  policy_version: 1,
};

export default function MerakliKontrolu() {
  const [policy, setPolicy] = useState<MerakliPolicy>(defaults);
  const [windowAllowed,setWindowAllowed]=useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [report, setReport] = useState<MerakliReport | null>(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiRequest<{ policy: MerakliPolicy; window_controls_allowed?: boolean }>("/parent/merakli-policy");
      setPolicy({ ...defaults, ...data.policy });
      setWindowAllowed(data.window_controls_allowed === true);
      try {
        setReport(await apiRequest<MerakliReport>("/parent/merakli-report"));
      } catch {
        setReport(null);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Ayarlar yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const save = async () => {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const data = await apiRequest<{ policy: MerakliPolicy; window_controls_allowed?: boolean }>("/parent/merakli-policy", {
        method: "PUT",
        body: policy,
      });
      setPolicy({ ...defaults, ...data.policy });
      setWindowAllowed(data.window_controls_allowed === true);
      setNotice("Ayarlar kaydedildi. Meraklı internete bağlandığında yeni kuralları otomatik alacak.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Ayarlar kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };

  const updateNumber = (key: "study_minutes_required" | "reward_minutes" | "warning_minutes", value: string) => {
    const parsed = Number(value);
    setPolicy((current) => ({ ...current, [key]: Number.isFinite(parsed) ? parsed : 0 }));
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Meraklı Kontrolü"
        description="Çalışma süresini, mola ödülünü ve çocuğunuzun bilgisayarındaki Meraklı kurallarını yönetin."
      />

      <MerakliMobileControl />
          <MerakliLiveStatus />
      {loading ? <EmptyState icon="ri-loader-4-line" title="Meraklı ayarları yükleniyor..." /> : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-5">
          <p className="text-sm text-red-800">{error}</p>
          <button type="button" onClick={() => void load()} className="mt-3 text-sm font-semibold text-red-800 underline">Tekrar dene</button>
        </div>
      ) : (
        <div className="max-w-3xl space-y-5">
          <div className="rounded-xl border border-background-200 bg-background-50 p-5 sm:p-6">
            <div className="flex gap-3">
              <i className="ri-timer-flash-line text-xl text-primary-600" aria-hidden="true" />
              <div>
                <h2 className="font-semibold text-foreground-900">Çalışma ve ödül döngüsü</h2>
                <p className="mt-1 text-sm leading-relaxed text-foreground-600">Meraklı, aktif ders çalışma süresini tamamlayınca çocuğunuza bilgisayarı serbestçe kullanabileceği bir mola verir. Mola bitince çalışma ekranı yeniden açılır.</p>
              </div>
            </div>
            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              <label className="block text-sm font-medium text-foreground-700">Çalışma süresi (dk)
                <input type="number" min="15" max="240" value={policy.study_minutes_required} onChange={(event) => updateNumber("study_minutes_required", event.target.value)} className="mt-1.5 w-full rounded-md border border-background-300 bg-white px-3 py-2 text-foreground-900" />
              </label>
              <label className="block text-sm font-medium text-foreground-700">Ödül molası (dk)
                <input type="number" min="5" max="60" value={policy.reward_minutes} onChange={(event) => updateNumber("reward_minutes", event.target.value)} className="mt-1.5 w-full rounded-md border border-background-300 bg-white px-3 py-2 text-foreground-900" />
              </label>
              <label className="block text-sm font-medium text-foreground-700">Bitiş uyarısı (dk)
                <input type="number" min="1" max="30" value={policy.warning_minutes} onChange={(event) => updateNumber("warning_minutes", event.target.value)} className="mt-1.5 w-full rounded-md border border-background-300 bg-white px-3 py-2 text-foreground-900" />
              </label>
            </div>
          </div>

          <div className="rounded-xl border border-background-200 bg-background-50 p-5 sm:p-6 space-y-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input type="checkbox" disabled={!windowAllowed} checked={Boolean(policy.enforced_study_mode)} onChange={(event) => setPolicy((current) => ({ ...current, enforced_study_mode: event.target.checked }))} className="mt-1 h-4 w-4" />
              <span><span className="block font-semibold text-foreground-900">Ders sırasında Meraklı tam ekran çalışsın</span><span className="mt-1 block text-sm text-foreground-600">Deneme sürümü her zaman normal ve kapatılabilir pencerede çalışır. Bu ayar yalnızca Plus/Premium üyelikte veli tarafından açılabilir. Etkin olduğunda pencere küçültme ve normal çıkış isteği veli PIN ekranına yönlendirilir. PIN, yalnızca çocuğun bilgisayarında ayarlanır.</span></span>
            </label>
            <label className="flex cursor-pointer items-start gap-3">
              <input type="checkbox" disabled={!windowAllowed || !policy.enforced_study_mode} checked={Boolean(policy.shutdown_on_exit)} onChange={(event) => setPolicy((current) => ({ ...current, shutdown_on_exit: event.target.checked }))} className="mt-1 h-4 w-4" />
              <span><span className="block font-semibold text-foreground-900">Yetkisiz çıkışta bilgisayarı kapat</span><span className="mt-1 block text-sm text-foreground-600">Bu seçenek açıkken veli PIN'i olmadan Meraklı'dan çıkma isteği bilgisayarı kapatma akışına dönüşür.</span></span>
            </label>
          </div>

          {notice && <p className="rounded-lg bg-accent-50 px-4 py-3 text-sm text-accent-800">{notice}</p>}
          <button type="button" onClick={() => void save()} disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60">
            <i className="ri-save-line" aria-hidden="true" />{saving ? "Kaydediliyor..." : "Meraklı Ayarlarını Kaydet"}
          </button>

          <section className="rounded-xl border border-background-200 bg-background-50 p-5 sm:p-6">
            <h2 className="font-semibold text-foreground-900">Meraklı gelişim özeti</h2>
            {!report ? <p className="mt-2 text-sm text-foreground-600">Henüz eşitlenmiş Meraklı çalışma kaydı yok.</p> : <>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg bg-white p-4"><p className="text-xs text-foreground-500">Son 7 gün ders</p><p className="mt-1 text-xl font-bold text-foreground-900">{report.summaries.reduce((total, row) => total + Number(row.lesson_minutes || 0), 0)} dk</p></div>
                <div className="rounded-lg bg-white p-4"><p className="text-xs text-foreground-500">Tamamlanan görev</p><p className="mt-1 text-xl font-bold text-foreground-900">{report.tasks.filter((task) => task.status === "completed").length}</p></div>
                <div className="rounded-lg bg-white p-4"><p className="text-xs text-foreground-500">Tekrar bekleyen konu</p><p className="mt-1 text-xl font-bold text-foreground-900">{report.review_topics.length}</p></div>
              </div>
              {report.review_topics.length > 0 && <div className="mt-5"><p className="text-sm font-semibold text-foreground-800">Öncelikli tekrarlar</p><div className="mt-2 flex flex-wrap gap-2">{report.review_topics.map((topic) => <span key={`${topic.subject}-${topic.topic}`} className="rounded-full bg-secondary-50 px-3 py-1 text-sm text-secondary-800">{topic.subject} · {topic.topic} ({topic.question_count})</span>)}</div></div>}
            </>}
          </section>
        </div>
      )}
    </div>
  );
}
